import {
  Badge,
  Box,
  Button,
  Card as ChakraCard,
  CardBody,
  Flex,
  Heading,
  HStack,
  IconButton,
  SimpleGrid,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { DeleteIcon, EditIcon } from '@chakra-ui/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Card from 'components/card/Card';
import { queryClient } from 'index';
import { deleteProductGroup, findProductGroups, ProductGroup } from 'services/api.service';
import GroupFormModal from './components/GroupFormModal';

export default function CatalogGroups() {
  const toast = useToast();
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingId, setEditingId] = useState<string | undefined>(undefined);
  const { data: groups, isLoading } = useQuery(['productGroups'], findProductGroups);

  const openCreate = () => {
    setEditingId(undefined);
    onOpen();
  };
  const openEdit = (id: string) => {
    setEditingId(id);
    onOpen();
  };
  const remove = async (id: string) => {
    if (!window.confirm('Remover este grupo?')) return;
    try {
      await deleteProductGroup(id);
      await queryClient.invalidateQueries({ queryKey: ['productGroups'] });
      toast({ title: 'Grupo removido', status: 'success', duration: 4000, isClosable: true });
    } catch (err: any) {
      toast({
        title: 'Erro',
        description: err?.response?.data?.message || 'Erro ao remover',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const sumPct = (g: ProductGroup) => (g.splits || []).reduce((a, s) => a + Number(s.percentage), 0);

  return (
    <Box>
      <Card>
        <Flex
          justify="space-between"
          align="center"
          mb={4}
        >
          <Box
            fontSize="lg"
            fontWeight="bold"
          >
            Grupos do catálogo
          </Box>
          <Button
            colorScheme="brand"
            onClick={openCreate}
          >
            Criar grupo
          </Button>
        </Flex>
        {!isLoading && (groups || []).length === 0 ? (
          <Text>Nenhum grupo cadastrado.</Text>
        ) : (
          <SimpleGrid
            columns={{ base: 1, sm: 2, md: 3, lg: 4 }}
            spacing={4}
          >
            {(groups || []).map((g) => (
              <ChakraCard
                key={g.id}
                variant="outline"
                cursor="pointer"
                onClick={() => navigate('/access/catalog/groups/' + g.id)}
                _hover={{ shadow: 'md', borderColor: 'brand.400' }}
              >
                <CardBody>
                  <Flex
                    justify="space-between"
                    align="start"
                    gap={2}
                  >
                    <Heading
                      size="sm"
                      noOfLines={2}
                    >
                      {g.name}
                    </Heading>
                    <HStack spacing={1}>
                      <IconButton
                        aria-label="Editar"
                        icon={<EditIcon />}
                        size="sm"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          openEdit(g.id);
                        }}
                      />
                      <IconButton
                        aria-label="Remover"
                        icon={<DeleteIcon />}
                        size="sm"
                        colorScheme="red"
                        variant="outline"
                        onClick={(e) => {
                          e.stopPropagation();
                          remove(g.id);
                        }}
                      />
                    </HStack>
                  </Flex>
                  <HStack
                    mt={3}
                    spacing={2}
                    wrap="wrap"
                  >
                    <Badge>{(g.splits || []).length} receb.</Badge>
                    <Badge colorScheme="purple">{sumPct(g)}% split</Badge>
                    {g.applyCashSplit ? <Badge colorScheme="green">Dinheiro</Badge> : null}
                  </HStack>
                </CardBody>
              </ChakraCard>
            ))}
          </SimpleGrid>
        )}
      </Card>
      {isOpen ? (
        <GroupFormModal
          isOpen={isOpen}
          onClose={onClose}
          groupId={editingId}
        />
      ) : null}
    </Box>
  );
}
